// src/views/admin/settings/item-categories/component/AddEditModel.jsx
import React, { useEffect, useState } from 'react';
import { useDispatch }                from 'react-redux';
import { useForm, Controller, useWatch } from 'react-hook-form';

import { Input }          from '@/components/ui/inputs/input';
import { InputTextarea }  from '@/components/ui/inputs/input-textarea';
import InputRadio         from '@/components/ui/inputs/input-radio';
import { InputSelect }    from '@/components/ui/inputs/input-select';
import CommonModal        from '@/components/ui/modals/CommonModal';
import ToastMe            from '@/components/ui/ToastMe';

import {
  addItemCategoryService,
  updateItemCategoryService,
  getByIdItemCategoryService,
} from '@/services/ItemCategoryServices';
import { fileUpload }   from '@/services/CommonService';
import validationErrors from '@/lib/validationErrors';

/* ─────────── select options ─────────── */
const natureOptions = [
  { value: 'consumable',    label: 'Consumable' },
  { value: 'durable',       label: 'Durable / Asset' },
  { value: 'miscellaneous', label: 'Miscellaneous' },
];

export default function AddEditModel({
  modelTitle,
  openModel,
  setOpenModel,
  getTableData,
  editDataId = '',
}) {
  const dispatch = useDispatch();

  /* ─────────── React-Hook-Form setup ─────────── */
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name          : '',
      description   : '',
      status        : 1,
      nature        : 'consumable',   // store as string
      trackInventory: true,
      isPerishable  : true,           // will be auto-reset below
      file_name     : '',
      tmp_file_name : '',
    },
  });

  /* keep UI reactive */
  const nature = useWatch({ control, name: 'nature' });

  /* ─────────── auto-reset perishable for non-consumables ─────────── */
  useEffect(() => {
    if (nature !== 'consumable') {
      // clear the perishable flag whenever they switch away
      setValue('isPerishable', false, { shouldValidate: false });
    }
  }, [nature, setValue]);

  const [isLoading,    setIsLoading]    = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  /* ─────────── populate form when editing ─────────── */
  useEffect(() => {
    if (!openModel) return;
    reset();  // clear previous values

    if (editDataId) {
      setIsLoading(true);
      dispatch(getByIdItemCategoryService(editDataId))
        .then(res => {
          const data = res?.data?.data || {};
          Object.entries(data).forEach(([k, v]) => setValue(k, v));
        })
        .catch(err => validationErrors(err.errorData, err.message))
        .finally(() => setIsLoading(false));
    }
  }, [openModel, editDataId, dispatch, reset, setValue]);

  /* ─────────── helpers ─────────── */
  const close = () => {
    setOpenModel(false);
    document.body.style.overflowY = 'auto';
    getTableData();
  };

  const handleFile = e => {
    const file = e.target.files?.[0];
    if (!file?.type.startsWith('image/')) {
      ToastMe('Please upload an image file', 'error');
      return;
    }
    dispatch(fileUpload({ file }, 'productCategory'))
      .then(r => setValue('file_name', r?.data?.fileName || ''))
      .catch(err => validationErrors(err.errorData, err.message));
  };

  const onSubmit = data => {
    setIsSubmitting(true);
    const svc = editDataId
      ? updateItemCategoryService(editDataId, data)
      : addItemCategoryService(data);

    dispatch(svc)
      .then(close)
      .catch(err => validationErrors(err.errorData, err.message))
      .finally(() => setIsSubmitting(false));
  };

  /* ─────────── render ─────────── */
  return (
    <CommonModal
      modelTitle={modelTitle}
      openModel={openModel}
      handleModelClose={close}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* NAME */}
          <Controller
            name="name"
            control={control}
            rules={{ required: true, minLength: 2, maxLength: 40 }}
            render={({ field }) => (
              <Input
                {...field}
                size="sm"
                labelText="Name"
                labelStyleClassName="db-field-title required"
                errorType={errors.name?.type}
                errorData={{ minLength: '2', maxLength: '40' }}
                groupClassName="sm:col-span-2"
              />
            )}
          />

          {/* IMAGE */}
          <Controller
            name="tmp_file_name"
            control={control}
            render={() => (
              <Input
                onChange={handleFile}
                size="sm"
                labelText="Image"
                type="file"
                errorType={errors.tmp_file_name?.type}
              />
            )}
          />

          {/* NATURE */}
          <Controller
            name="nature"
            control={control}
            rules={{ required: true }}
            render={({ field }) => {
              const selected = natureOptions.find(o => o.value === field.value) || null;
              return (
                <InputSelect
                  id="nature"
                  size="sm"
                  labelText="Nature"
                  labelStyleClassName="db-field-title required"
                  options={natureOptions}
                  value={selected}
                  onChange={opt => field.onChange(opt?.value)}
                />
              );
            }}
          />

          {/* TRACK INVENTORY */}
          <div className="form-input-item">
            <label className="db-field-title required">Track Inventory?</label>
            <div className="db-field-radio-group">
              <Controller
                name="trackInventory"
                control={control}
                render={({ field }) => (
                  <>
                    <InputRadio
                      id="trackYes"
                      labelText="Yes"
                      checked={!!field.value}
                      onChange={() => field.onChange(true)}
                    />
                    <InputRadio
                      id="trackNo"
                      labelText="No"
                      checked={!field.value}
                      onChange={() => field.onChange(false)}
                    />
                  </>
                )}
              />
            </div>
          </div>

          {/* PERISHABLE? — only for consumables */}
          {nature === 'consumable' && (
            <div className="form-input-item">
              <label className="db-field-title required">Perishable?</label>
              <div className="db-field-radio-group">
                <Controller
                  name="isPerishable"
                  control={control}
                  render={({ field }) => (
                    <>
                      <InputRadio
                        id="perishYes"
                        labelText="Yes"
                        checked={!!field.value}
                        onChange={() => field.onChange(true)}
                      />
                      <InputRadio
                        id="perishNo"
                        labelText="No"
                        checked={!field.value}
                        onChange={() => field.onChange(false)}
                      />
                    </>
                  )}
                />
              </div>
            </div>
          )}

          {/* STATUS */}
          <div className="form-input-item">
            <label className="db-field-title required">Status</label>
            <div className="db-field-radio-group">
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <>
                    <InputRadio
                      id="statusActive"
                      labelText="Active"
                      checked={Number(field.value) === 1}
                      onChange={() => field.onChange(1)}
                    />
                    <InputRadio
                      id="statusInactive"
                      labelText="Inactive"
                      checked={Number(field.value) === 2}
                      onChange={() => field.onChange(2)}
                    />
                  </>
                )}
              />
            </div>
          </div>

          {/* DESCRIPTION */}
          <Controller
            name="description"
            control={control}
            rules={{ required: true, minLength: 5, maxLength: 200 }}
            render={({ field }) => (
              <InputTextarea
                {...field}
                rows={5}
                size="sm"
                labelText="Description"
                labelStyleClassName="db-field-title required"
                className="h-auto sm:col-span-2"
                errorType={errors.description?.type}
                errorData={{ minLength: '5', maxLength: '200' }}
              />
            )}
          />
        </div>

        {/* BUTTONS */}
        <div className="flex gap-3 mt-6">
          <button
            type="submit"
            className="db-btn py-2 text-white bg-primary"
            disabled={isSubmitting || isLoading}
          >
            <i className="lab lab-save" /> {isSubmitting ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            className="modal-btn-outline modal-close"
            onClick={close}
          >
            <i className="lab lab-close" /> Close
          </button>
        </div>
      </form>
    </CommonModal>
  );
}
